import SelectInput, { OptionType } from '@oberoninternal/travelbase-ds/components/form/SelectInput';
import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import { Box, Flex } from '@rebass/grid';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import Dropdown from './designsystem/Dropdown';
import { Input } from '@oberoninternal/travelbase-ds/components/form/TextInput';
import { gql } from '@apollo/client';
import { useAccommodationQuery } from '../generated/graphql';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { useFormikContext } from 'formik';
import { SearchBarValues } from './SearchBox';
import { useDebounce } from 'use-debounce';

export const query = gql`
    query accommodation($q: String!, $limit: Int, $offset: Int) {
        accommodations(query: $q, limit: $limit, offset: $offset) {
            id
            name
        }
    }
`;

interface Props {
    fullHeight?: boolean;
    onChange?: (accoLabel: string) => void;
}

const AccommodationDropdown: FC<React.PropsWithChildren<Props>> = ({ fullHeight = false, onChange = () => null }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const [debouncedQuery] = useDebounce(searchQuery, 200);
    const sesame = useSesame(false, { closeOnClickOutside: true });
    const { data } = useAccommodationQuery({ variables: { q: debouncedQuery, limit: 100 } });

    const accommodationOptions = useMemo(() => {
        const accs = data?.accommodations ?? [];
        return accs.map(a => ({ label: a.name, value: a.id }));
    }, [data?.accommodations]);

    const { values, setFieldValue } = useFormikContext<SearchBarValues>();
    const [selectedLabel, setSelectedLabel] = useState('');
    const selectedAccommodation = accommodationOptions.find(
        accommodationOption => accommodationOption.value === values.accommodationId
    );

    useEffect(() => {
        if (!values.accommodationId) {
            setSelectedLabel('');
            return;
        }
        if (selectedAccommodation) {
            setSelectedLabel(selectedAccommodation.label);
            onChange(selectedAccommodation.label);
        }
    }, [values.accommodationId, selectedAccommodation, onChange]);

    return (
        <StyledDropdown
            ref={sesame.ref}
            fullHeight={fullHeight}
            onClickCapture={e => {
                if (sesame.open && e.detail === 0) {
                    e.stopPropagation();
                }
            }}
            onToggle={() => {
                setSearchQuery('');
                if (sesame.open) {
                    inputRef.current?.blur();
                    sesame.onClose();
                    return;
                }
                sesame.onOpen();
                inputRef.current?.focus();
            }}
            open={sesame.open}
            surfaceElement={
                <Flex flexWrap="wrap">
                    <StyledBody variant="tiny">
                        <FormattedMessage defaultMessage="Locatie" />
                    </StyledBody>
                    <Box width={1}>
                        <Box>
                            <StyledSearchInput
                                ref={inputRef}
                                type="search"
                                value={searchQuery}
                                placeholder={
                                    sesame.open ? 'Selecteer een locatie' : selectedLabel || 'Selecteer een locatie'
                                }
                                onChange={q => {
                                    setSearchQuery(q.target.value.toLowerCase());
                                }}
                            />
                        </Box>
                    </Box>
                </Flex>
            }
            borderRadius={0}
        >
            <StyledSelectInput
                hideChoices
                value={accommodationOptions.find(option => option.value === values.accommodationId) ?? null}
                options={accommodationOptions}
                onChange={(selected: OptionType) => {
                    setSearchQuery('');
                    setFieldValue('accommodationId', selected.value === values.accommodationId ? null : selected.value);
                    inputRef.current?.blur();
                    sesame.onClose();
                }}
                name="accommodationId"
                thinShadow
                menuPlacement="bottom"
                menuIsOpen
                color="primary"
                placeholder={<FormattedMessage defaultMessage="Locatie" />}
                components={{
                    Control: () => null,
                }}
            />
        </StyledDropdown>
    );
};

export default AccommodationDropdown;

const StyledSearchInput = styled(Input)`
    box-shadow: none;
    padding: 0;

    &::placeholder {
        color: black;
    }

    &::-webkit-search-cancel-button {
        display: none;
    }
`;

const StyledDropdown = styled(Dropdown)`
    --borderRadius: 0;
    padding-left: 3.2rem;
`;

const StyledSelectInput = styled(SelectInput)`
    & div {
        position: relative;
        margin-top: 0;

        @media screen and (max-height: 768px) {
            max-height: 150px;
        }
    }
`;

const StyledBody = styled(Body)`
    color: ${({ theme }) => theme.colors.neutral['60']};
    font-weight: 300 !important;
    line-height: 1.15;
    white-space: nowrap;
`;
